import React from 'react';
import { LayoutGrid, List, Pencil, Trash2, UserCircle, X } from 'lucide-react';
import { Passenger } from '../../models/PassengerModel';
import DataTable, { DataTableColumn } from '../../components/Base/DataTable';
import CompanyCell from '../../components/Base/CompanyCell';
import PassengerCard from './components/PassengerCard';
import { isPlaceholderAddress } from '../../components/Passengers/bulkPassengerValidation';
import '../../components/Base/DataTable.css';
import './styles/PassengersTable.css';

export type PassengersViewMode = 'cards' | 'table';

type Props = {
  data: Passenger[];
  loading?: boolean;
  onEdit: (p: Passenger) => void;
  onDelete: (id: number) => void;
  sortOrder?: 'asc' | 'desc';
  sortField?: 'name' | 'first_lastname';
  sortAnimationKey?: number;
  sortAnimationTarget?: string;
  onSortToggle?: (field: 'name' | 'first_lastname') => void;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  totalCount?: number;
  searchTerm?: string;
  onClearSearch?: () => void;
  viewMode: PassengersViewMode;
  onViewModeChange: (mode: PassengersViewMode) => void;
};

const renderSortableHeader = (
  field: 'name' | 'first_lastname',
  label: string,
  sortOrder?: 'asc' | 'desc',
  sortField?: 'name' | 'first_lastname',
  sortAnimationKey?: number,
  sortAnimationTarget?: string,
  onSortToggle?: (field: 'name' | 'first_lastname') => void
) => {
  if (!onSortToggle) return label;
  const isAnimating = sortAnimationTarget === field;
  const isActive = sortField === field;

  return (
    <button
      key={isAnimating ? `${field}-${sortAnimationKey}` : field}
      type="button"
      className={`table-sort-header${isAnimating ? ' is-sorting' : ''}`}
      onClick={() => onSortToggle(field)}
      aria-label={`Ordenar por ${label}`}
    >
      {label}
      {isActive ? (
        <span className="table-sort-indicator">{sortOrder === 'desc' ? 'Z-A' : 'A-Z'}</span>
      ) : null}
    </button>
  );
};

function fullName(row: Passenger): string {
  return [row.name, row.first_lastname, row.second_lastname]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

const PassengersTable: React.FC<Props> = ({
  data,
  loading,
  onEdit,
  onDelete,
  sortOrder,
  sortField,
  sortAnimationKey,
  sortAnimationTarget,
  onSortToggle,
  toolbar,
  footer,
  totalCount,
  searchTerm = '',
  onClearSearch,
  viewMode,
  onViewModeChange,
}) => {
  const term = searchTerm.trim();
  const shown = data.length;
  const total = totalCount ?? shown;

  const viewToggle = (
    <div className="passengers-view-toggle" role="group" aria-label="Cambiar vista del listado">
      <button
        type="button"
        className={`passengers-view-toggle__btn${viewMode === 'cards' ? ' is-active' : ''}`}
        onClick={() => onViewModeChange('cards')}
        aria-pressed={viewMode === 'cards'}
        title="Vista de tarjetas"
      >
        <LayoutGrid size={16} strokeWidth={2} aria-hidden />
        <span>Cards</span>
      </button>
      <button
        type="button"
        className={`passengers-view-toggle__btn${viewMode === 'table' ? ' is-active' : ''}`}
        onClick={() => onViewModeChange('table')}
        aria-pressed={viewMode === 'table'}
        title="Vista de tabla"
      >
        <List size={16} strokeWidth={2} aria-hidden />
        <span>Tabla</span>
      </button>
    </div>
  );

  const summary = !loading ? (
    <>
      <span className="data-table__summary-label">
        {term
          ? `${shown} resultado${shown === 1 ? '' : 's'} en esta página`
          : `Mostrando ${shown} de ${total} pasajeros`}
      </span>
      {term ? (
        <span className="data-table__summary-chip">
          <UserCircle size={14} strokeWidth={2.25} aria-hidden />
          <span className="data-table__summary-chip-text">Filtro: “{term}”</span>
          {onClearSearch ? (
            <button
              type="button"
              className="data-table__summary-clear"
              onClick={onClearSearch}
              aria-label="Limpiar filtros de búsqueda"
              title="Limpiar filtro"
            >
              <X size={14} strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
        </span>
      ) : null}
      {viewToggle}
    </>
  ) : (
    viewToggle
  );

  const columns: DataTableColumn<Passenger>[] = [
    {
      id: 'id',
      header: 'ID',
      width: '88px',
      label: 'ID',
      cell: (row) => <span className="data-table__id">{row.id ?? '—'}</span>,
    },
    {
      id: 'company_name',
      header: 'Empresa',
      label: 'Empresa',
      cell: (row) => <CompanyCell name={row.company_name} />,
    },
    {
      id: 'name',
      header: renderSortableHeader(
        'name',
        'Nombre',
        sortOrder,
        sortField,
        sortAnimationKey,
        sortAnimationTarget,
        onSortToggle
      ),
      label: 'Nombre',
      cell: (row) => (
        <span className="data-table__primary" title={row.name}>
          {row.name?.trim() || '—'}
        </span>
      ),
    },
    {
      id: 'first_lastname',
      header: renderSortableHeader(
        'first_lastname',
        'Primer apellido',
        sortOrder,
        sortField,
        sortAnimationKey,
        sortAnimationTarget,
        onSortToggle
      ),
      label: 'Primer apellido',
      cell: (row) => (
        <span className="data-table__primary" title={row.first_lastname}>
          {row.first_lastname?.trim() || '—'}
        </span>
      ),
    },
    {
      id: 'second_lastname',
      header: 'Segundo apellido',
      label: 'Segundo apellido',
      cell: (row) => (
        <span className="data-table__muted" title={row.second_lastname}>
          {row.second_lastname?.trim() || '—'}
        </span>
      ),
    },
    {
      id: 'phone',
      header: 'Celular',
      label: 'Celular',
      width: '140px',
      cell: (row) => <span className="data-table__muted">{row.phone?.trim() || '—'}</span>,
    },
    {
      id: 'addresses',
      header: 'Direcciones',
      label: 'Direcciones',
      cell: (row) => {
        const realAddresses = (row.addresses ?? []).filter(
          (a) => a.street && !isPlaceholderAddress(a.street)
        );
        if (realAddresses.length === 0) {
          return <span className="data-table__muted">—</span>;
        }
        return (
          <span className="data-table__muted" title={realAddresses.map((a) => a.street).join(', ')}>
            {realAddresses.map((a) => a.street).join(' · ')}
          </span>
        );
      },
    },
  ];

  if (viewMode === 'table') {
    return (
      <DataTable<Passenger>
        className="passengers-data-table"
        columns={columns}
        data={data}
        getRowId={(row) => row.id ?? fullName(row)}
        loading={loading}
        emptyMessage={term ? 'Sin pasajeros para esta búsqueda' : 'Sin pasajeros'}
        emptyDescription={
          term
            ? 'Prueba con otro filtro o limpia la búsqueda.'
            : 'Crea un nuevo pasajero para comenzar.'
        }
        toolbar={toolbar}
        summary={summary}
        footer={footer}
        actionsHeader="Acciones"
        aria-label="Tabla de pasajeros"
        renderActions={(row) => (
          <>
            <button
              type="button"
              className="data-table__action data-table__action--edit"
              onClick={() => onEdit(row)}
              title="Editar pasajero"
              aria-label={`Editar ${fullName(row) || 'pasajero'}`}
            >
              <Pencil size={16} strokeWidth={2} aria-hidden />
              <span>Editar</span>
            </button>
            {row.id != null ? (
              <button
                type="button"
                className="data-table__action data-table__action--danger"
                onClick={() => onDelete(Number(row.id))}
                title="Eliminar pasajero"
                aria-label={`Eliminar ${fullName(row) || 'pasajero'}`}
              >
                <Trash2 size={16} strokeWidth={2} aria-hidden />
                <span>Eliminar</span>
              </button>
            ) : null}
          </>
        )}
      />
    );
  }

  return (
    <div className="data-table passengers-data-table">
      <div className="data-table__surface">
        {toolbar ? <div className="data-table__toolbar">{toolbar}</div> : null}
        {summary ? (
          <div className="data-table__summary" role="status" aria-live="polite">
            {summary}
          </div>
        ) : null}

        <div className="passengers-card-grid" aria-label="Listado de pasajeros en tarjetas">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={`sk-${i}`} className="passenger-card-skeleton" aria-hidden>
                <span className="passenger-card-skeleton__avatar" />
                <span className="passenger-card-skeleton__line" />
                <span className="passenger-card-skeleton__line passenger-card-skeleton__line--sm" />
                <span className="passenger-card-skeleton__line passenger-card-skeleton__line--md" />
              </div>
            ))
          ) : data.length === 0 ? (
            <div className="passengers-card-empty">
              <span className="data-table__empty-title">
                {term ? 'Sin pasajeros para esta búsqueda' : 'Sin pasajeros'}
              </span>
              <span className="data-table__empty-desc">
                {term
                  ? 'Prueba con otro filtro o limpia la búsqueda.'
                  : 'Crea un nuevo pasajero para comenzar.'}
              </span>
            </div>
          ) : (
            data.map((passenger) => (
              <PassengerCard
                key={passenger.id ?? fullName(passenger)}
                passenger={passenger}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          )}
        </div>

        {footer ? <div className="data-table__footer">{footer}</div> : null}
      </div>
    </div>
  );
};

export default PassengersTable;
